import type { TranslationsMap } from "../types/language";

export interface CompensationObject {
  currency: string;
  /** The importers send null when a job posts a starting salary only. */
  max?: number | string | null;
  min?: number | string | null;
  period: string;
}

const CURRENCY_SYMBOLS: Record<string, string> = {
  EUR: "€",
  EURO: "€",
  CZK: "Kč",
  HUF: "Ft",
  SEK: "kr",
  GBP: "£",
  RON: "lei",
  USD: "$",
};

const CURRENCY_PREFIX = new Set(["GBP", "USD"]);

/** Keeps the amount and its currency symbol on the same line. */
const NBSP = "\u00a0";

/** The ATS sends the currency as free text, e.g. "EUR", "Euro" or " eur". */
function normalizeCurrency(currency: string): string {
  return currency.trim().toUpperCase();
}

function toPositiveNumber(
  value: number | string | null | undefined
): number | null {
  if (value == null) {
    return null;
  }
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function formatAmount(
  amount: number,
  currency: string,
  locale: string
): string {
  // Hourly rates arrive with cents ("31.73"); annual figures are whole numbers
  // and must not grow a ",00" tail.
  const fractionDigits = Number.isInteger(amount) ? 0 : 2;
  const number = new Intl.NumberFormat(locale, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  }).format(amount);
  const code = normalizeCurrency(currency);
  const symbol = CURRENCY_SYMBOLS[code] ?? currency.trim();
  return CURRENCY_PREFIX.has(code)
    ? `${symbol}${number}`
    : `${number}${NBSP}${symbol}`;
}

export type SalaryState =
  | { type: "info-only" }
  | { type: "fall-a"; value: number; currency: string; period: string }
  | {
      type: "fall-b";
      min: number;
      max: number;
      currency: string;
      period: string;
    };

export function getSalaryState(
  comp: CompensationObject | null | undefined
): SalaryState {
  if (comp == null || !comp.currency || !comp.period) {
    return { type: "info-only" };
  }
  const min = toPositiveNumber(comp.min);
  if (min == null) {
    return { type: "info-only" };
  }
  const max = toPositiveNumber(comp.max);
  if (max != null && max > min) {
    return {
      type: "fall-b",
      min,
      max,
      currency: comp.currency,
      period: comp.period,
    };
  }
  return {
    type: "fall-a",
    value: min,
    currency: comp.currency,
    period: comp.period,
  };
}

/**
 * English fallbacks for every translation key this module reads. A client that
 * has not translated a key yet renders English instead of `undefined`.
 */
const ENGLISH_FALLBACKS: Record<string, string> = {
  salary_from_prefix: "from",
  salary_label_min_only: "Starting salary",
  salary_label_range: "Salary range",
  salary_period_per_year: "per year",
  salary_period_per_month: "per month",
  salary_period_bi_weekly: "bi-weekly",
  salary_period_per_week: "per week",
  salary_period_per_day: "per day",
  salary_period_per_hour: "per hour",
};

function translate(key: string, t: TranslationsMap): string {
  return t[key] ?? ENGLISH_FALLBACKS[key] ?? key;
}

export function formatSalaryRow(
  state: SalaryState,
  t: TranslationsMap,
  locale: string
): { label: string; value: string } | null {
  if (state.type === "info-only") {
    return null;
  }

  const period = periodLabel(state.period, t);

  if (state.type === "fall-a") {
    return {
      label: translate("salary_label_min_only", t),
      value: `${translate("salary_from_prefix", t)} ${formatAmount(state.value, state.currency, locale)} ${period}`,
    };
  }
  return {
    label: translate("salary_label_range", t),
    value: `${formatAmount(state.min, state.currency, locale)} – ${formatAmount(state.max, state.currency, locale)} ${period}`,
  };
}

/**
 * Period vocabularies differ per ATS: SuccessFactors sends "Per Year",
 * TalentLink sends "yearly". Keys are matched case-insensitively; an unknown
 * period renders verbatim.
 */
const PERIOD_KEYS: Record<string, string> = {
  "per year": "salary_period_per_year",
  yearly: "salary_period_per_year",
  annually: "salary_period_per_year",
  annual: "salary_period_per_year",
  year: "salary_period_per_year",
  "per month": "salary_period_per_month",
  monthly: "salary_period_per_month",
  month: "salary_period_per_month",
  "bi-weekly": "salary_period_bi_weekly",
  biweekly: "salary_period_bi_weekly",
  fortnightly: "salary_period_bi_weekly",
  "per week": "salary_period_per_week",
  weekly: "salary_period_per_week",
  week: "salary_period_per_week",
  "per day": "salary_period_per_day",
  daily: "salary_period_per_day",
  day: "salary_period_per_day",
  "per hour": "salary_period_per_hour",
  hourly: "salary_period_per_hour",
  hour: "salary_period_per_hour",
};

function periodLabel(period: string, t: TranslationsMap): string {
  const key = PERIOD_KEYS[period.trim().toLowerCase()];
  return key ? translate(key, t) : period;
}
